import { captureException } from "@sentry/react";
import { render } from "dom-serializer";
import { AnyNode, Document, Element } from "domhandler";
import { find, findOne, getAttributeValue, isTag, textContent } from "domutils";
import JSZip, { JSZipObject } from "jszip";
import { v4 as uuid } from "uuid";

import { Cardinality, Shape } from "@/generated/graphql";

import { ParsedQuestion } from "../../data";
import { getElementByPath, getParsedXML } from "../utils";
import { convertQTIQuestion } from "./common";
import {
  ConditionVar,
  QTIFeedback,
  QTIMaterial,
  QTIMetadata,
  QTIOutcomes,
  QTIPromptImage,
  QTIQuestion,
  QTIResource,
  QTIResponse,
  QTIResponseCondition,
  QTIResponseContent,
} from "./types";

/**
 * Parse QTIMetadata from qti files in both blackboard package file
 * and QTI1.x file
 **/
function parseMetadata(rawQuestion: Element): QTIMetadata {
  const itemmetadata = findOne(
    (e) => isTag(e) && e.tagName.toLowerCase() === "itemmetadata",
    rawQuestion.childNodes,
    false
  );
  if (!itemmetadata) return {};
  const qtiMetadataFields = find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "qtimetadatafield",
    itemmetadata.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  const bbMetadataFields = find(
    (e) => isTag(e) && /(bbmd|qmd)/i.test(e.tagName),
    itemmetadata.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  if (qtiMetadataFields.length > 0) {
    return qtiMetadataFields.reduce((metadatas, metadata) => {
      const label = findOne(
        (e) => e.tagName.toLowerCase() === "fieldlabel",
        metadata.childNodes,
        false
      );
      const value = findOne(
        (e) => e.tagName.toLowerCase() === "fieldentry",
        metadata.childNodes,
        false
      );
      if (label && value) {
        metadatas[textContent(label)] = textContent(value);
      }
      return metadatas;
    }, {} as QTIMetadata);
  } else if (bbMetadataFields.length > 0) {
    return bbMetadataFields.reduce((metadatas, metadata) => {
      metadatas[metadata.tagName.toLowerCase().replace(/(bbmd|qmd)_/, "")] =
        textContent(metadata);
      return metadatas;
    }, {} as QTIMetadata);
  }
  return {};
}
// Find zero or one direct presentation element child of a parentElement
function getPresentation(parentElement: Element) {
  return findOne(
    (e) => e.tagName === "presentation",
    parentElement.childNodes,
    false
  );
}

// Map response's element rcardinality attribute to Cardinality enum
function getResponseCardinality(response: Element) {
  const rawCardinality = getAttributeValue(response, "rcardinality");
  switch (rawCardinality?.toLowerCase()) {
    case "multiple": {
      return Cardinality.Multiple;
    }
    case "ordered": {
      return Cardinality.Ordered;
    }
    case "single":
    default:
      return Cardinality.Single;
  }
}

// Parse feedbacks from qti's itemfeedback element(s)
function parseFeedbacks(rawQuestion: Element) {
  return find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "itemfeedback",
    rawQuestion.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ).map((e) => {
    const element = e as Element;
    return {
      id: getAttributeValue(element, "ident") ?? "",
      contents: getMaterials(element),
    } satisfies QTIFeedback;
  });
}

/*
 * Parse choices, word limit, shuffle from qti responses
 **/
function parseResponses(question: Element) {
  const presentation = getPresentation(question);
  if (!presentation) return [];

  const responses = find(
    (e) =>
      isTag(e) &&
      /response_(xy|str|num|grp|extension|na|lid)/.test(
        e.tagName.toLowerCase()
      ),
    presentation.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];

  return responses.map((r) => {
    const id = getAttributeValue(r, "ident") ?? uuid();
    const cardinality = getResponseCardinality(r);
    const type = r.tagName.toLowerCase().split("_")?.[1];
    const wordLimitMax = getAttributeValue(r, "word_limit_max");

    const response: QTIResponse = {
      id,
      cardinality,
      type,
      wordLimit: wordLimitMax ? parseInt(wordLimitMax, 10) : undefined,
    };
    const choicesElement = findOne(
      (e) => isTag(e) && e.tagName.toLowerCase() === "render_choice",
      r.childNodes,
      false
    );

    const hotspotElement = findOne(
      (e) => isTag(e) && e.tagName.toLowerCase() === "render_hotspot",
      r.childNodes,
      false
    );

    const isFlowChild =
      r.parent &&
      isTag(r.parent) &&
      r.parent.tagName === "flow" &&
      r.parent.attribs.class === "Block";

    let materialElement = findOne(
      (e) => isTag(e) && e.tagName.toLowerCase() === "material",
      r.childNodes,
      false
    );

    let rightMatchBlocks: AnyNode[] | undefined;

    // A hack to parse matching from blackboard
    // that appears as child of a flow element with
    // class == "RIGHT_MATCH_BLOCK"
    if (isFlowChild && r.parent) {
      materialElement = getElementByPath(
        [r.parent],
        ["flow", "flow", "material"]
      );
      rightMatchBlocks = find(
        (e) =>
          isTag(e) &&
          e.tagName === "flow" &&
          e.attribs.class === "Block" &&
          !!e.parent &&
          isTag(e.parent) &&
          e.parent.tagName === "flow" &&
          e.parent.attribs.class === "RIGHT_MATCH_BLOCK",
        question.childNodes,
        true,
        Number.MAX_SAFE_INTEGER
      );
    }

    if (choicesElement) {
      const shuffle = getAttributeValue(choicesElement, "shuffle");
      const responseLabels = find(
        (e) => isTag(e) && e.tagName.toLowerCase() === "response_label",
        choicesElement.childNodes,
        true,
        Number.MAX_SAFE_INTEGER
      ) as Element[];

      response.choiceResponse = {
        shuffle: shuffle && shuffle === "Yes" ? true : false,
        choices: responseLabels.flatMap((rl, index) =>
          parseResponse(rl, index, rightMatchBlocks)
        ),
        contents: materialElement ? parseMaterial(materialElement) : undefined,
      };
    }
    if (hotspotElement) {
      const responseLabels = find(
        (e) => isTag(e) && e.tagName.toLowerCase() === "response_label",
        hotspotElement.childNodes,
        true,
        Number.MAX_SAFE_INTEGER
      ) as Element[];
      response.correctAreas = responseLabels.map((label) => {
        const identifier = label.attribs.ident ?? uuid();
        const shape = label.attribs.rarea ?? Shape.Point;
        const coords = textContent(label).split(",").map(Number);
        return {
          identifier,
          shape,
          coords,
        };
      });
    }
    return response;
  });
}

/**
 * Recursively parse nested QTI materials
 **/
function getMaterials(element: Element): QTIMaterial[] {
  const maybeMaterials = find(
    (e) =>
      isTag(e) &&
      [
        "flow",
        "flow_mat",
        "flow_label",
        "material",
        "material_ref",
        "response_xy",
      ].includes(e.tagName.toLowerCase()),
    element.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  return (
    maybeMaterials
      // Removed materials/flows that only contains responses
      .filter((m) => !m.attribs.class?.match(/(MATCH|RESPONSE)_BLOCK$/))
      .flatMap((m) => {
        if (m.tagName.toLowerCase() === "material") {
          return parseMaterial(m);
        }
        // TODO: parse material_ref as well
        return getMaterials(m);
      })
  );
}

function parseResponse(
  responseLabel: Element,
  index: number,
  matchBlocks?: AnyNode[]
): QTIResponseContent[] {
  const rawMaterial =
    index !== undefined && matchBlocks?.[index]
      ? getMaterials(matchBlocks[index] as Element)
      : getMaterials(responseLabel);

  if (rawMaterial) {
    return [
      {
        id: getAttributeValue(responseLabel, "ident") ?? uuid(),
        contents: rawMaterial.flat(),
      },
    ];
  }
  return [];
}

function getResprocessing(rawQuestion: Element) {
  return findOne(
    (e) => isTag(e) && e.tagName.toLowerCase() === "resprocessing",
    rawQuestion.childNodes,
    false
  );
}

function parseOutcomes(
  resprocessing: Element,
  metadata: QTIMetadata
): QTIOutcomes {
  const absoluteMaxPoints = (metadata?.["absolutescore_max"] ??
    metadata?.["points_possible"]) as unknown as string | undefined;
  const decVars = find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "decvar",
    resprocessing.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];

  const outcomes = decVars.reduce((outcomes, decVar) => {
    const name = getAttributeValue(decVar, "varname");
    const type = getAttributeValue(decVar, "vartype");
    const minvalue = getAttributeValue(decVar, "minvalue");
    const maxvalue = getAttributeValue(decVar, "maxvalue");
    const defaultvalue = getAttributeValue(decVar, "defaultval");
    const cutValue = getAttributeValue(decVar, "cutvalue");
    if (name && type) {
      outcomes[name] = {
        type,
        min: parseNumberVar(type, minvalue),
        max: parseNumberVar(type, maxvalue),
        default: parseNumberVar(type, defaultvalue) ?? 0,
        cut: parseNumberVar(type, cutValue),
      };
    }
    return outcomes;
  }, {} as QTIOutcomes);
  const outcomesTotalPoints = Object.entries(outcomes).reduce(
    (acc, [_name, { max }]) => acc + (max ?? 0),
    0
  );
  return Object.entries(outcomes).reduce((acc, [name, outcome]) => {
    acc[name] = {
      ...outcome,
      max:
        absoluteMaxPoints && outcome.max && outcomesTotalPoints
          ? (outcome.max / outcomesTotalPoints) * parseFloat(absoluteMaxPoints)
          : !outcome.max || outcome.max >= 100
            ? 1
            : outcome.max,
    };
    return acc;
  }, {} as QTIOutcomes);
}

function parseNumberVar(type: string, decVar?: string): number | undefined {
  if (!decVar) return;
  switch (type) {
    case "Decimal": {
      return parseFloat(decVar);
    }
    case "integer": {
      return parseInt(decVar, 10);
    }
    default: {
      return;
    }
  }
}

function parseRespConditions(resprocessing: Element): QTIResponseCondition[] {
  const respConditions = find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "respcondition",
    resprocessing.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];

  return respConditions.flatMap((r) => {
    const respCondition = parseRespCondition(r);
    return respCondition ? [respCondition] : [];
  });
}

function parseRespCondition(
  respCondition: Element
): QTIResponseCondition | undefined {
  const setVars = find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "setvar",
    respCondition.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  const conditionVars = find(
    (e) => isTag(e) && e.tagName.toLowerCase() === "conditionvar",
    respCondition.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  // if (setVars?.length === 0) return;
  return {
    id: getAttributeValue(respCondition, "ident"),
    vars: setVars.map((v) => ({
      name: getAttributeValue(v, "varname") ?? "SCORE",
      value: textContent(v),
      action: getAttributeValue(v, "action"),
    })),
    conditions: conditionVars.flatMap((c) => parseRespConditionCondition(c)),
  };
}

function parseRespConditionCondition(
  conditionElement: Element
): ConditionVar[] {
  return find(
    (e) =>
      isTag(e) &&
      [
        "and",
        "or",
        "not",
        "varequal",
        "varlt",
        "varlte",
        "vargt",
        "vargte",
      ].includes(e.tagName.toLowerCase()),
    conditionElement.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ).map((e) => {
    const element = e as Element;
    const tagName = element.tagName.toLowerCase();
    if (["and", "or", "not"].includes(tagName)) {
      return {
        operation: tagName,
        conditions: parseRespConditionCondition(element),
      };
    } else {
      return {
        condition: {
          value: textContent(element),
          id: getAttributeValue(element, "respident") ?? "",
          comparator: getRespComparator(tagName),
        },
      };
    }
  });
}

function getRespComparator(varElementTagName: string) {
  if (varElementTagName === "varequal") {
    return "eq";
  } else if (
    ["vargt", "varlt", "vargte", "varlte"].includes(varElementTagName)
  ) {
    return varElementTagName.replace("var", "");
  }
  return;
}

function parseMaterial(material: Element): QTIMaterial[] {
  const materials = find(
    (e) =>
      isTag(e) &&
      /(mattext|matemtext|matbreak|matimage|mataudio|matvideo|matapplet|matapplication|mat_extension|mat_formattedtext|altmaterial)/i.test(
        e.tagName
      ),
    material.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  return materials.flatMap((m) => {
    const tagName = m.tagName.toLowerCase();
    if (["mat_extension", "altmaterial"].includes(tagName)) {
      return parseMaterial(m);
    } else if (
      ["mattext", "matemtext", "mat_formattedtext"].includes(tagName)
    ) {
      const type = ["mat_formattedtext", "mattext"].includes(tagName)
        ? "text"
        : "emphasized text";
      const textType =
        tagName === "mat_formattedtext"
          ? getAttributeValue(m, "type")
          : getAttributeValue(m, "texttype");
      const text = textContent(m);
      return [
        {
          type,
          textType,
          text,
        },
      ] satisfies QTIMaterial[];
    } else {
      return [
        {
          type: tagName.replace("mat", ""),
        },
      ] satisfies QTIMaterial[];
    }
  });
}

function getPromptImage(rawQuestion: Element): QTIPromptImage | undefined {
  const hotspot = findOne(
    (e) => isTag(e) && e.tagName === "render_hotspot",
    rawQuestion.childNodes
  );
  if (!hotspot) return;
  const image = findOne(
    (e) => isTag(e) && e.tagName === "matimage",
    hotspot.childNodes
  );
  if (!image) return;
  return {
    attributes: image.attribs,
    content: textContent(image),
  };
}

/**
 * Parse individual qti's item as QTIQuestion
 **/
function parseQTIQuestion(rawQuestion: Element): QTIQuestion | undefined {
  const responses = parseResponses(rawQuestion);
  const metadata = parseMetadata(rawQuestion);
  const presentation = getPresentation(rawQuestion);
  const resprocessing = getResprocessing(rawQuestion);
  const promptImage = getPromptImage(rawQuestion);

  const feedbacks = parseFeedbacks(rawQuestion);
  if (!presentation) return undefined;
  const contents = getMaterials(presentation);
  if (!resprocessing)
    return {
      responses,
      metadata,
      contents,
      promptImage,
      feedbacks,
      respConditions: [],
      outcomes: {},
    };
  const outcomes = parseOutcomes(resprocessing, metadata);
  const respConditions = parseRespConditions(resprocessing);
  return {
    responses,
    metadata,
    contents,
    promptImage,
    outcomes,
    respConditions,
    feedbacks,
  };
}

async function parseSection(
  section: Element,
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
): Promise<Omit<ParsedQuestion, "fileOriginId">[]> {
  if (!section) return [];
  const sections = find(
    (e) => isTag(e) && e.tagName === "section",
    section.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];

  const sectionsQuestions = sections.length
    ? (await parseSections(sections, file, zip, resources)).flat()
    : [];
  const questionElements = find(
    (e) => isTag(e) && e.tagName === "item",
    section.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  return [
    ...(
      await Promise.all(
        questionElements.flatMap(async (currQuestion) => {
          try {
            const qtiQuestion = parseQTIQuestion(currQuestion);
            const parsedQuestion = await convertQTIQuestion(
              qtiQuestion,
              file,
              zip,
              resources
            );

            if (parsedQuestion) return [parsedQuestion];
            return [] as Omit<ParsedQuestion, "fileOriginId">[];
          } catch (error) {
            captureException("Error parsing qti questionItem", {
              extra: {
                error,
                questionItem: render(currQuestion),
              },
            });
            return [] as Omit<ParsedQuestion, "fileOriginId">[];
          }
        })
      )
    ).flat(),
    ...sectionsQuestions,
  ];
}

async function parseSections(
  sections: Element[],
  file?: JSZipObject | null,
  zip?: JSZip,
  resources?: QTIResource[]
) {
  return (
    await Promise.all(
      sections.flatMap(
        async (section) => await parseSection(section, file, zip, resources)
      )
    )
  ).flat();
}

async function getResources(zip: JSZip): Promise<QTIResource[]> {
  const resources = [];
  for (const [_, zipEntry] of Object.entries(zip.files)) {
    if (zipEntry.name.includes(".xml")) {
      try {
        const xml = await zipEntry.async("text");
        const parsed = getParsedXML(xml);
        const resourceElement = findOne(
          (e) => isTag(e) && e.tagName === "resource",
          parsed.childNodes,
          true
        );
        if (resourceElement) {
          const path = zipEntry.name.replace(/\.xml$/, "");
          const file = zip.file(path);
          if (file) {
            const lastSlashIndex = file.name.lastIndexOf("/");
            const filename = file.name.substring(lastSlashIndex + 1);
            resources.push({
              path,
              filename,
            });
          }
        }
      } catch (_e) {
        continue;
      }
    }
  }
  return resources;
}
/**
 * parse section like(section, objectbank & assessment) objects from qti xml
 **/
async function parseQuestions(
  parsedXML: Document,
  file?: JSZipObject | null,
  zip?: JSZip
) {
  const root = findOne(
    (e) => e.tagName === "questestinterop",
    parsedXML.childNodes,
    false
  );
  if (!root) return [];
  const assessments = find(
    (e) => isTag(e) && e.tagName === "assessment",
    root.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  const resources = zip ? await getResources(zip) : [];
  if (assessments && Array.isArray(assessments) && assessments.length > 0) {
    return await Promise.all(
      assessments.flatMap(async (assessment) => {
        const section = findOne(
          (e) => e.tagName === "section",
          assessment.childNodes,
          false
        );
        return section ? await parseSection(section, file, zip, resources) : [];
      })
    );
  } else {
    const sectionOrQuestionBank = findOne(
      (e) => isTag(e) && ["section", "objectbank"].includes(e.tagName),
      root.childNodes,
      false
    );
    return sectionOrQuestionBank
      ? await parseSection(sectionOrQuestionBank, file, zip, resources)
      : [];
  }
}

export const parser = {
  parseQuestions,
};
